import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { db } from "config/firebase";
import { executePrognosAutomationRemote } from "api/automations.api";
import { toast } from "react-toastify";
import { Link as RouterLink } from "react-router-dom";
import {
  FiRefreshCw,
  FiCpu,
  FiCalendar,
  FiExternalLink,
  FiUser,
  FiGlobe,
  FiClock,
  FiInbox,
  FiSliders,
  FiCheckCircle,
} from "react-icons/fi";
import PredictionModal from "components/news/PredictionModal";
import PromiseModal from "components/news/PromiseModal";

export default function NewsPage() {
  const [newsDocs, setNewsDocs] = useState([]);
  const [selectedDay, setSelectedDay] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalKind, setModalKind] = useState(null); // "prediction" or "promise"
  const [isScanning, setIsScanning] = useState(false);

  const fetchNews = async () => {
    try {
      const q = query(
        collection(db, "predictions-ai"),
        orderBy("created_at", "desc"),
        limit(7),
      );
      const snap = await getDocs(q);
      const docs = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setNewsDocs(docs);
      if (docs.length > 0 && !selectedDay) {
        setSelectedDay(docs[0].id);
      }
    } catch (err) {
      console.error("Error fetching news:", err);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const handleRunScanNow = async () => {
    setIsScanning(true);
    toast.info("Գործարկվում է համացանցի ավտոմատ որոնումը և AI վերլուծությունը...");
    try {
      const result = await executePrognosAutomationRemote(null, {
        name: "News Quick Scan",
        targetEntities: ["predictions", "promises"],
        searchConfig: {
          query: "կանխատեսում OR խոստանում եմ OR հայտարարել է",
          topic: "general",
          maxResults: 15,
          includeDomains: ["news.am", "armenpress.am", "azatutyun.am", "civilnet.am", "1lurer.am", "aravot.am", "pastv.am", "tert.am", "168.am"],
        },
        autoDraft: false,
      });
      if (result?.success) {
        toast.success(`Հաջողությամբ գտնվել է ${result.findingsCount || 0} նոր նյութ:`);
        await fetchNews();
      } else {
        toast.error("Որոնումը չհաջողվեց:");
      }
    } catch (err) {
      toast.error(`Սխալ՝ ${err.message}`);
    } finally {
      setIsScanning(false);
    }
  };

  const handleOpenModal = (finding) => {
    setModalData(finding);
    setModalKind(finding.kind);
    setModalOpen(true);
  };

  const currentDoc = newsDocs.find((doc) => doc.id === selectedDay);

  return (
    <div className="w-full min-h-screen bg-slate-50 p-6 sm:p-8 lg:p-10 overflow-y-auto">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* 1. Header & AI Automation Action Bar */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden shadow-lg">
          <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />

          <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <span className="p-3.5 rounded-2xl bg-indigo-600/20 text-indigo-400 border border-indigo-500/30 shrink-0">
                <FiCpu size={26} />
              </span>
              <div>
                <div className="flex items-center gap-2.5">
                  <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight">
                    AI Լրահոս և Սկաներ
                  </h1>
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                    Live Feed
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-xl leading-relaxed">
                  Համացանցից (News.am, Armenpress, Azatutyun և այլն) ինքնաշխատ սկանավորված հրապարակումներ, որոնցից AI-ն առանձնացրել է կանխատեսումներն ու խոստումները:
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <button
                onClick={handleRunScanNow}
                disabled={isScanning}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white transition shadow-sm cursor-pointer"
              >
                <FiRefreshCw className={isScanning ? "animate-spin" : ""} size={15} />
                <span>{isScanning ? "Որոնում է..." : "Գործարկել ակնթարթային սկան"}</span>
              </button>

              <RouterLink
                to="/automations"
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white transition border border-slate-700/80"
              >
                <FiSliders size={14} />
                <span>Կարգավորումներ</span>
              </RouterLink>
            </div>
          </div>
        </div>

        {/* 2. Day Selector Pills / Bar */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-4 sm:p-5 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <FiCalendar className="text-slate-400" size={18} />
              <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
                Սկանավորման օրեր
              </span>
            </div>

            {/* Day selector segmented pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
              {newsDocs.map((doc) => {
                const isSelected = selectedDay === doc.id;
                const count = doc.findings?.length || doc.count || 0;
                return (
                  <button
                    key={doc.id}
                    onClick={() => setSelectedDay(doc.id)}
                    className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition whitespace-nowrap cursor-pointer ${
                      isSelected
                        ? "bg-indigo-600 text-white shadow-xs shadow-indigo-600/20"
                        : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                    }`}
                  >
                    <span>{doc.id}</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                        isSelected
                          ? "bg-white/20 text-white"
                          : "bg-slate-200 text-slate-600"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* 3. News for Selected Day */}
        {currentDoc ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <h2 className="text-base sm:text-lg font-bold text-slate-900">
                  {currentDoc.id} ամսաթվի արդյունքներ
                </h2>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                  {currentDoc.findings?.length || 0} նյութ
                </span>
              </div>
            </div>

            {currentDoc.findings?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {currentDoc.findings.map((finding, idx) => {
                  const isPrediction = finding.kind === "prediction";
                  const title =
                    finding.publishTitle || finding.title || "Անվերնագիր";
                  const formattedDate = finding.publishedAt
                    ? new Date(finding.publishedAt).toLocaleDateString("hy-AM", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : null;

                  return (
                    <div
                      key={idx}
                      className="bg-white border border-slate-200/80 hover:border-indigo-300 rounded-2xl p-5 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between group"
                    >
                      <div className="space-y-3">
                        {/* Tags / Meta Top Row */}
                        <div className="flex items-center justify-between gap-2">
                          <span
                            className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${
                              isPrediction
                                ? "bg-purple-50 text-purple-700 border-purple-200"
                                : "bg-blue-50 text-blue-700 border-blue-200"
                            }`}
                          >
                            {isPrediction ? "Կանխատեսում" : "Խոստում"}
                          </span>

                          {finding.site && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 font-medium">
                              <FiGlobe size={11} className="text-slate-400" />
                              {finding.site}
                            </span>
                          )}
                        </div>

                        {/* Title */}
                        <h3
                          onClick={() => handleOpenModal(finding)}
                          className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors line-clamp-2 leading-snug cursor-pointer"
                        >
                          {title}
                        </h3>

                        {/* Summary */}
                        {finding.publishSummary && (
                          <p className="text-xs text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-100 line-clamp-3 leading-relaxed">
                            {finding.publishSummary}
                          </p>
                        )}

                        {/* Author & Date Metadata */}
                        <div className="flex flex-wrap items-center gap-3 pt-1 text-xs text-slate-500">
                          {finding.authorName && (
                            <span className="inline-flex items-center gap-1 font-medium text-slate-700">
                              <FiUser size={13} className="text-slate-400" />
                              {finding.authorName}
                            </span>
                          )}
                          {formattedDate && (
                            <span className="inline-flex items-center gap-1 text-slate-400">
                              <FiClock size={12} />
                              {formattedDate}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Card Actions */}
                      <div className="pt-4 mt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                        <button
                          type="button"
                          onClick={() => handleOpenModal(finding)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition shadow-xs cursor-pointer"
                        >
                          <FiCheckCircle size={14} />
                          <span>Դիտել / Ստեղծել</span>
                        </button>

                        {finding.link && (
                          <a
                            href={finding.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Բացել սկզբնաղբյուրը"
                            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition border border-slate-200/80 cursor-pointer"
                          >
                            <FiExternalLink size={15} />
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white border border-slate-200/80 rounded-2xl p-12 text-center max-w-md mx-auto shadow-xs">
                <span className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto mb-3">
                  <FiInbox size={22} />
                </span>
                <h3 className="text-sm font-bold text-slate-900 mb-1">
                  Արդյունքներ չկան
                </h3>
                <p className="text-xs text-slate-500">
                  Այս օրվա համար գտնված լրահոսային նյութեր առկա չեն:
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white border border-slate-200/80 rounded-2xl p-12 text-center max-w-md mx-auto shadow-xs">
            <span className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-500 flex items-center justify-center mx-auto mb-3">
              <FiInbox size={22} />
            </span>
            <h3 className="text-sm font-bold text-slate-900 mb-1">
              Տվյալներ չեն գտնվել
            </h3>
            <p className="text-xs text-slate-500">
              Սեղմեք «Գործարկել ակնթարթային սկան» կոճակը՝ վերջին նորությունները սկանավորելու համար:
            </p>
          </div>
        )}

        {/* Modal Handlers */}
        {modalData && modalKind === "prediction" && (
          <PredictionModal
            data={modalData}
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
          />
        )}

        {modalData && modalKind === "promise" && (
          <PromiseModal
            data={modalData}
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
          />
        )}
      </div>
    </div>
  );
}
